/**
 * `:::` marker expansion for the PREVIEW pane — a port of picaluna-v3's
 * src/lib/renderMarkdown.ts.
 *
 * The preview called marked.parse directly, so an author who centred a line or
 * coloured a word saw the raw `::: colour-blue …` text in the pane that exists
 * to answer "what will this look like on the website". The thing it was least
 * able to preview was the thing hardest to picture.
 *
 * DUPLICATION IS DELIBERATE and the risk is real: Conductor cannot import from
 * picaluna-v3. The rules below MUST match renderMarkdown.ts — if the marker
 * vocabulary changes there, change it here. The preview showing something the
 * site will not render is worse than no preview.
 *
 * Only the marker passes are ported. Sanitising is not: this HTML goes into a
 * preview pane in an app the author is already authenticated to, and the site
 * sanitises on its own side at build time.
 */
/* eslint-disable */

const MARKER_CLASS: Record<string, string> = {
  center: "text-center",
  centre: "text-center", // the spelling an Australian author will reach for
  right: "text-right",
  left: "text-left",
  indent: "md:px-12 lg:px-24",
  "colour-forest": "text-forest",
  "colour-orange": "text-orange",
  "colour-blue": "text-blue",
  "colour-off-white": "text-off-white",
  "colour-brand-black": "text-brand-black",
  "colour-white": "text-white",
}

/**
 * One or more marker names → the class string, or null if ANY name is unknown.
 *
 * Several names on one marker (`::: center colour-orange`) because wanting a
 * heading both centred and coloured is the common case, and the alternative
 * was nesting two fences around one line. An unknown name in the list makes
 * the WHOLE marker unrecognised, so it stays visible as written rather than
 * half-applying — `::: center colour-ornage` is a typo the author must see,
 * not a silently-centred-but-not-orange heading.
 */
function markerClasses(names: string): string | null {
  const parts = names.trim().split(/[ \t]+/).filter(Boolean)
  if (!parts.length) return null
  const out: string[] = []
  for (const n of parts) {
    const cls = MARKER_CLASS[n]
    if (cls === undefined) return null
    if (cls) out.push(cls)
  }
  return out.join(" ")
}

/**
 * Fenced code blocks are left alone, so a page can DOCUMENT the markers.
 *
 * Inline code (`` `::: center` ``) was already safe — the span pass skips a
 * line's code spans — but a ``` fence was not: the marker inside it expanded
 * and the docs page rendered a real centred div inside its own <pre>.
 */
function isFenceDelimiter(line: string): boolean {
  return /^[ \t]*(```|~~~)/.test(line)
}

/**
 * Mid-sentence spans: `some ::: colour-orange $395 ::: words` → a <span>.
 *
 * Same `:::` vocabulary as the block form rather than a second syntax, because
 * an author who has learned one marker should not have to learn another to
 * colour three words. The difference is position, not spelling: a marker at
 * the START of a line opens a block, one INSIDE a line styles a span.
 *
 * Only styling names are eligible. `center`/`right`/`left`/`indent` are
 * excluded — text-align on an inline span does nothing (alignment is a
 * property of the block box), so accepting them would emit markup that
 * silently has no effect.
 *
 * Runs BEFORE expandBlockMarkers so a line that is entirely one marker is
 * still read as a block; this pass requires text on at least one side.
 */
const INLINE_ONLY = new Set(
  Object.keys(MARKER_CLASS).filter((k) => k.startsWith("colour-"))
)

/** Every name must be colour-* for a span; alignment has no meaning inline. */
function inlineClasses(names: string): string | null {
  const parts = names.trim().split(/[ \t]+/).filter(Boolean)
  if (!parts.length || !parts.every((n) => INLINE_ONLY.has(n))) return null
  return markerClasses(names)
}

/**
 * Inline spans INSIDE a one-line block marker.
 *
 * The outer `::: name … :::` is left for the block pass; only markers between
 * its delimiters are expanded, so the result is a block whose body already
 * carries its <span>s. Without this a nested marker reached marked as plain
 * text and published as visible `:::` punctuation.
 */
function expandNestedSpans(line: string): string {
  const open = line.indexOf(":::")
  const close = line.lastIndexOf(":::")
  if (open === -1 || close <= open) return line

  // Skip past the outer marker's NAMES so they are not treated as content.
  const afterOpen = line.slice(open + 3)
  const words = afterOpen.replace(/^[ \t]+/, "").split(/([ \t]+)/)
  let consumed = afterOpen.length - afterOpen.replace(/^[ \t]+/, "").length
  for (let i = 0; i < words.length; i += 2) {
    if (MARKER_CLASS[words[i]] === undefined) break
    consumed += words[i].length + (words[i + 1]?.length ?? 0)
  }
  const bodyStart = open + 3 + consumed
  if (bodyStart >= close) return line

  const body = line.slice(bodyStart, close)
  if (!body.includes(":::")) return line

  const expanded = body.replace(
    /:::[ \t]+([^\n]*?)[ \t]*:::/g,
    (whole, inner: string) => {
      const w = inner.trim().split(/[ \t]+/)
      const names: string[] = []
      while (w.length && INLINE_ONLY.has(w[0])) names.push(w.shift()!)
      const text = w.join(" ")
      if (!names.length || !text) return whole
      const cls = inlineClasses(names.join(" "))
      return cls ? `<span class="${cls}">${text}</span>` : whole
    }
  )
  return line.slice(0, bodyStart) + expanded + line.slice(close)
}

function expandInlineMarkers(md: string): string {
  if (!md.includes(":::")) return md
  let inFence = false
  return md
    .split("\n")
    .map((line) => {
      if (isFenceDelimiter(line)) {
        inFence = !inFence
        return line
      }
      if (inFence) return line
      /* A line that starts with ::: and is ONLY that marker belongs to the
         block pass. But `::: colour-orange X ::: then more words` is an
         inline span followed by prose — the block pass would swallow the
         trailing text into the div, so it is handled here instead. The test
         is whether anything survives after the marker's closing :::. */
      const trimmed = line.trim()
      if (/^:::/.test(trimmed)) {
        const asBlock = /^:::[ \t]+[a-z-]+[ \t]+[^\n]*?[ \t]*:::[ \t]*$/.test(trimmed)
        const opensBlock = /^:::[ \t]*([a-z-]+(?:[ \t]+[a-z-]+)*)?[ \t]*$/.test(trimmed)
        /* A one-line marker is handed to the block pass — but its CONTENTS may
           hold another marker, and the block pass only reads the outer one.
           `::: colour-forest Lorem ::: colour-blue sim ::: orci :::` published
           the inner marker as literal punctuation on the page. Expand the
           inside first and let the block pass have the rest. */
        if (asBlock) return expandNestedSpans(line)
        if (opensBlock) return line
      }
      /* The body is captured WHOLE and its leading words split off as names
         afterwards, rather than letting the names group match greedily.

         A greedy `([a-z-]+(?:\s+[a-z-]+)*)` swallowed the CONTENT: colouring
         the single word "transfer" produced `::: colour-orange transfer :::`,
         whose names read as "colour-orange transfer" — not a valid list, so
         the whole marker was rejected and published as literal text. It only
         appeared to work when the coloured text was capitalised or numeric,
         which is why "GST" and "$395" passed and "transfer" did not. */
      return line.replace(
        /:::[ \t]+([^\n]*?)[ \t]*:::/g,
        (whole, body: string) => {
          const words = body.trim().split(/[ \t]+/)
          const names: string[] = []
          while (words.length && INLINE_ONLY.has(words[0])) names.push(words.shift()!)
          const text = words.join(" ")
          if (!names.length || !text) return whole
          const cls = inlineClasses(names.join(" "))
          if (!cls) return whole
          return `<span class="${cls}">${text}</span>`
        }
      )
    })
    .join("\n")
}

/**
 * Split `::: a b some text :::` into its marker names and its body.
 *
 * Words are consumed as names only while they ARE names, so the first word
 * that is not in the table ends the list and begins the text. Returns null
 * when the line is not a marker at all, or when the first word is unknown —
 * an unrecognised marker stays visible rather than half-applying.
 */
function takeNames(trimmed: string): { cls: string; text: string } | null {
  const m = /^:::[ \t]+(.*?)[ \t]*(?::::[ \t]*)?$/.exec(trimmed)
  if (!m || !m[1]) return null
  const words = m[1].split(/[ \t]+/)
  const names: string[] = []
  while (words.length && MARKER_CLASS[words[0]] !== undefined) names.push(words.shift()!)
  if (!names.length) return null
  const cls = markerClasses(names.join(" "))
  if (cls === null) return null
  return { cls, text: words.join(" ") }
}

/** `::: name` … `:::` → `<div class="…">` … `</div>`, before marked runs. */
function expandBlockMarkers(md: string): string {
  const lines = md.split("\n")
  const out: string[] = []
  const open: boolean[] = []
  let inFence = false
  for (const rawLine of lines) {
    /* Inside a ``` fence nothing is a marker — that is what lets a page
       document the syntax without rendering it. */
    if (isFenceDelimiter(rawLine)) {
      inFence = !inFence
      out.push(rawLine)
      continue
    }
    if (inFence) {
      out.push(rawLine)
      continue
    }
    /* Inline form: `::: name  …text…  :::` all on one line. Rewritten to the
       three-line block form and handled by the same code below, so there is
       one set of rules about what a marker means. The trailing `:::` is
       optional — an author who opens inline and closes on a later line still
       gets what they meant. */
    /* Names are taken one word at a time and checked against the table, rather
       than grabbed by a greedy group: `::: center orange is the colour` has a
       first word that IS a marker name and a second that is not, so only
       "center" is the name and the rest is body text. A greedy group swallowed
       the whole sentence and matched nothing.

       A line that is ONLY names opens a block and is left to `start` below. */
    const oneLine = takeNames(rawLine.trim())
    const inline = oneLine && oneLine.text ? oneLine : null
    let line = rawLine
    const inlineCls = inline ? inline.cls : null
    if (inline && inlineCls !== null && inline.text) {
      const closed = /:::[ \t]*$/.test(rawLine.trim())
      out.push(`<div class="${inlineCls}">`, "", inline.text, "")
      if (closed) out.push("</div>")
      else open.push(true)
      continue
    }
    const start = /^:::[ \t]+([a-z-]+(?:[ \t]+[a-z-]+)*)[ \t]*$/.exec(line.trim())
    const startCls = start ? markerClasses(start[1]) : null
    if (start && startCls !== null) {
      // A blank line each side keeps marked treating the contents as block
      // markdown; without it the first paragraph fuses onto the <div>.
      out.push(`<div class="${startCls}">`, "")
      open.push(true)
      continue
    }
    if (line.trim() === ":::" && open.length) {
      out.push("", "</div>")
      open.pop()
      continue
    }
    out.push(line)
  }
  // Close anything the author left open rather than emitting unbalanced HTML
  // — the sanitizer would drop the stray tag and silently eat the styling.
  while (open.length) {
    out.push("", "</div>")
    open.pop()
  }
  return out.join("\n")
}

/** Markdown with every marker expanded, ready for marked.parse. */
export function expandMarkers(md: string): string {
  return expandBlockMarkers(expandInlineMarkers(md));
}
